import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Eye, Power } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageTitle, Panel } from "./AdminShell";
import Modal, { TextField, SelectField } from "@/components/Modal";
import RichEditor from "@/components/RichEditor";
import { ImageUploader } from "@/components/common";
import { toast } from "sonner";

export default function AdminPages() {
  const [pages, setPages] = useState(null);
  const [cols, setCols] = useState([]);
  const [edit, setEdit] = useState(null); // page obj or {} for new

  const load = useCallback(() => {
    api.get("/admin/pages").then((r) => setPages(r.data));
    api.get("/footer/columns").then((r) => setCols(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (p) => {
    try { await api.put(`/admin/pages/${p.id}`, { ...p, active: !p.active }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Supprimer la page « ${p.title} » ?`)) return;
    try { await api.delete(`/admin/pages/${p.id}`); toast.success("Page supprimée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!pages) return null;
  const byCol = {};
  pages.forEach((p) => { (byCol[p.column] = byCol[p.column] || []).push(p); });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <PageTitle>Pages & Contenus</PageTitle>
        <button onClick={() => setEdit({})} className="bg-[#C85A32] text-white text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1" data-testid="add-page-btn"><Plus className="w-4 h-4" /> Nouvelle page</button>
      </div>
      <div className="space-y-5">
        {cols.map((c) => (
          <div key={c.id}>
            <h3 className="text-xs font-bold tracking-wider text-slate-500 mb-2">{c.label}</h3>
            <div className="space-y-2">
              {(byCol[c.key] || []).map((p) => (
                <Panel key={p.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`page-row-${p.slug}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{p.title}</p>
                    <p className="text-xs text-slate-400 truncate">/{p.href ? `→ ${p.href}` : `pages/${p.slug}`}</p>
                  </div>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${p.status === "published" && p.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`} data-testid={`page-status-${p.slug}`}>
                    {p.status === "draft" ? "Brouillon" : p.active ? "Publiée" : "Désactivée"}
                  </span>
                  <div className="ml-auto flex gap-1">
                    {!p.href && <a href={`/pages/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded hover:bg-slate-100 text-slate-500" title="Prévisualiser" data-testid={`page-preview-${p.slug}`}><Eye className="w-4 h-4" /></a>}
                    <button onClick={() => toggle(p)} className="p-2 rounded hover:bg-slate-100 text-slate-500" title="Activer/Désactiver" data-testid={`page-toggle-${p.slug}`}><Power className="w-4 h-4" /></button>
                    <button onClick={() => setEdit(p)} className="p-2 rounded hover:bg-slate-100 text-slate-500" title="Modifier" data-testid={`page-edit-${p.slug}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(p)} className="p-2 rounded hover:bg-red-50 text-red-500" title="Supprimer" data-testid={`page-del-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </Panel>
              ))}
            </div>
          </div>
        ))}
      </div>
      {edit && <PageForm page={edit} cols={cols} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}

function PageForm({ page, cols, onClose, onDone }) {
  const isEdit = !!page.id;
  const [f, setF] = useState({ title: "", slug: "", content: "", image: "", column: cols[0]?.key || "", href: "", order: 0, status: "published", active: true, ...page });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) await api.put(`/admin/pages/${page.id}`, { ...f, order: Number(f.order) });
      else await api.post("/admin/pages", { ...f, order: Number(f.order) });
      toast.success(isEdit ? "Page mise à jour" : "Page créée");
      onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={isEdit ? "Modifier la page" : "Nouvelle page"} onClose={onClose} testid="page-form" size="lg">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Titre" value={f.title} onChange={set("title")} testid="pg-title" />
        <TextField label="Slug (laisser vide pour auto)" value={f.slug} onChange={set("slug")} testid="pg-slug" required={false} />
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Colonne du footer" value={f.column} onChange={set("column")} options={cols.map((c) => ({ value: c.key, label: c.label }))} testid="pg-column" />
          <TextField label="Ordre" type="number" value={f.order} onChange={set("order")} testid="pg-order" required={false} />
        </div>
        <TextField label="Lien direct (optionnel, ex: /recherche)" value={f.href} onChange={set("href")} testid="pg-href" required={false} />
        {!f.href && (
          <div>
            <span className="text-sm font-semibold text-slate-700 block mb-1">Contenu</span>
            <RichEditor value={f.content} onChange={set("content")} testid="pg-content" />
          </div>
        )}
        <div><span className="text-sm font-semibold text-slate-700 block mb-1.5">Image (optionnelle)</span><ImageUploader value={f.image ? [f.image] : []} onChange={(imgs) => set("image")(imgs[0] || "")} multiple={false} testid="pg-image" /></div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Statut" value={f.status} onChange={set("status")} options={[{ value: "published", label: "Publiée" }, { value: "draft", label: "Brouillon" }]} testid="pg-status" />
          <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={f.active} onChange={(e) => set("active")(e.target.checked)} data-testid="pg-active" /> Active</label>
        </div>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="pg-submit">{isEdit ? "Enregistrer" : "Créer la page"}</button>
      </form>
    </Modal>
  );
}
